import { defineStore } from 'pinia';
import { billingService } from '@shared/api';
import type { Subscription } from '@features/billing/api/billing.service.types';
import { handleAxiosError } from '@shared/utils/error';

let fetchCurrentSubscriptionPromise: Promise<void> | null = null;

interface BillingState {
  currentSubscription: Subscription | null;
  isLoading: boolean;
  initialized: boolean;
  error: string | null;
}

export const useBillingStore = defineStore('billing', {
  state: (): BillingState => ({
    currentSubscription: null,
    isLoading: false,
    initialized: false,
    error: null,
  }),
  getters: {
    isFree: (state) => !state.currentSubscription?.plan.slug,
    planSlug: (state) =>
      state.currentSubscription?.plan.slug.toLowerCase() ?? 'free',
  },
  actions: {
    async fetchCurrentSubscription(force = false) {
      if (fetchCurrentSubscriptionPromise && !force) {
        return fetchCurrentSubscriptionPromise;
      }

      if (force) {
        this.initialized = false;
      }

      const promise = (async () => {
        this.isLoading = true;
        this.error = null;

        try {
          const { data } = await billingService.getCurrentSubscription();
          this.currentSubscription = data;
        } catch (e) {
          this.currentSubscription = null;
          this.error = 'Failed to load subscription';
          handleAxiosError(e);
        } finally {
          this.isLoading = false;
          this.initialized = true;
          fetchCurrentSubscriptionPromise = null;
        }
      })();

      fetchCurrentSubscriptionPromise = promise;

      try {
        await promise;
      } catch (e) {
        handleAxiosError(e);
      }
    },

    async upgradeSubscription(planSlug: string, interval: 'month' | 'year') {
      try {
        this.isLoading = true;
        await billingService.upgradeSubscription(planSlug, interval);
        await this.fetchCurrentSubscription();
      } catch (e) {
        handleAxiosError(e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async downgradeSubscription(planSlug: string, interval: 'month' | 'year') {
      try {
        this.isLoading = true;
        await billingService.downgradeSubscription(planSlug, interval);
        await this.fetchCurrentSubscription();
      } catch (e) {
        handleAxiosError(e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async cancelSubscription() {
      try {
        this.isLoading = true;
        await billingService.cancelSubscription();
        await this.fetchCurrentSubscription();
      } catch (e) {
        handleAxiosError(e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async reactivateSubscription() {
      try {
        this.isLoading = true;
        await billingService.reactivateSubscription();
        await this.fetchCurrentSubscription();
      } catch (e) {
        handleAxiosError(e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
